/**
 * V1.9.7 — Symbol Layer
 *
 * Extracted from FloorPlan2D.jsx. Renders every placed floor-plan symbol
 * (furniture, appliances, plumbing fixtures, stairs, misc), with:
 *   - selection halo + per-corner resize handles when selected
 *   - selection label showing label + dims + override-height + hotkey hint
 *   - drag-to-move via onSymbolPointerDown (parent)
 *   - resize via onResizeStart (parent)
 *
 * All state lives in the parent — this component is pure-presentational.
 *
 * Props:
 *   symbols:         array of placed symbol records (id, type, x, z, ...)
 *   scale:           px per metre
 *   toX(worldX):     screen px (X)
 *   toY(worldZ):     screen px (Y, flipped)
 *   selectedSymbolId
 *   moveSymbolId
 *   measurementSystem
 *   onSymbolPointerDown(e, sym)
 *   onResizeStart(e, sym, corner)
 *   fmtSize(w_m, d_m, system): string
 */
import React from "react";
import { getSymbol } from "../lib/symbols";

export default function SymbolLayer({
  symbols,
  scale,
  toX,
  toY,
  selectedSymbolId,
  moveSymbolId,
  measurementSystem,
  onSymbolPointerDown,
  onResizeStart,
  fmtSize,
}) {
  return (
    <>
      {symbols.map((s) => {
        const sym = getSymbol(s.type);
        if (!sym) return null;
        // V1.9.3 — honour per-instance resize overrides.
        const w_m = s.override_w_m || sym.width_m;
        const d_m = s.override_d_m || sym.depth_m;
        const wPx = w_m * scale;
        const hPx = d_m * scale;
        const cxWorld = s.x + w_m / 2;
        const czWorld = s.z + d_m / 2;
        const cxPx = toX(cxWorld);
        const cyPx = toY(czWorld);
        const isSel = s.id === selectedSymbolId;
        return (
          <g
            key={s.id}
            data-testid={`floor-plan-symbol-${s.id}`}
            data-symbol-type={s.type}
            transform={`translate(${cxPx - wPx / 2}, ${cyPx - hPx / 2}) rotate(${s.rotation_deg || 0}, ${wPx / 2}, ${hPx / 2})`}
            onPointerDown={(e) => onSymbolPointerDown(e, s)}
            onClick={(e) => e.stopPropagation()}
            style={{ cursor: moveSymbolId === s.id ? "grabbing" : "grab" }}
          >
            {isSel && (
              <rect
                x={-4}
                y={-4}
                width={wPx + 8}
                height={hPx + 8}
                fill="rgba(196, 169, 107, 0.10)"
                stroke="#c4a96b"
                strokeWidth="1.5"
                strokeDasharray="4 3"
                pointerEvents="none"
              />
            )}
            {sym.render({ w: wPx, h: hPx })}
            {/* V1.9.3 — Resize handles at the 4 corners (visible when selected). */}
            {isSel && ["nw", "ne", "sw", "se"].map((corner) => {
              const hx = corner.includes("e") ? wPx : 0;
              const hy = corner.includes("s") ? hPx : 0;
              const cursor =
                corner === "nw" || corner === "se" ? "nwse-resize" : "nesw-resize";
              return (
                <rect
                  key={corner}
                  data-testid={`symbol-resize-${corner}`}
                  data-symbol-id={s.id}
                  x={hx - 5}
                  y={hy - 5}
                  width={10}
                  height={10}
                  fill="#c4a96b"
                  stroke="#66522b"
                  strokeWidth="1"
                  onPointerDown={(e) => onResizeStart(e, s, corner)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ cursor }}
                />
              );
            })}
            {isSel && (
              <text
                x={wPx / 2}
                y={hPx + 18}
                textAnchor="middle"
                fontSize="9"
                fontFamily="JetBrains Mono, monospace"
                fill="#c4a96b"
                pointerEvents="none"
              >
                {sym.label} · {fmtSize(w_m, d_m, measurementSystem)}
                {/* V1.9.7 — read override_h_m from the INSTANCE (s), not the
                    catalog (sym). The catalog has no per-symbol height — it
                    has width_m + depth_m only — so the previous read of
                    `sym.override_h_m` was always undefined and never showed. */}
                {Number.isFinite(s.override_h_m) ? ` · H ${s.override_h_m.toFixed(2)}m` : ""}
                {" · ← →   [ ]   0   DEL"}
              </text>
            )}
          </g>
        );
      })}
    </>
  );
}
